import { QUERY_KEYS } from '@/lib/cache-keys/queryKeys'
import { UserApi } from 'api/api'
import { useAuth } from 'context/JWTContext'
import { useQuery } from 'react-query'
import { LeaderboardResponse, PositionData } from 'types/types'

const extractWinner = (leaderboard?: LeaderboardResponse) => ({
  winner: leaderboard?.items?.[0]?.user?.nickname ?? null,
  winnerScore: leaderboard?.items?.[0]?.result ?? null,
})

// When index !== position the user shares their rank with someone else.
const isPositionTied = (positionData?: PositionData) =>
  !!positionData && positionData.index !== positionData.position

export const useScoreboardData = (gameId?: number, roundId?: number, needsLeaderboard = true) => {
  const { isAuthenticated } = useAuth()
  const { data: gamePositionData } = useQuery(
    [QUERY_KEYS.POSITION_DATA, gameId],
    () => UserApi.getPositionData(gameId ?? 0, undefined),
    {
      refetchInterval: 1000 * 60,
      enabled: isAuthenticated && !!gameId,
      staleTime: 60000,
    },
  )

  const { data: roundPositionData } = useQuery(
    [QUERY_KEYS.POSITION_DATA, roundId, gameId],
    () => UserApi.getPositionData(gameId ?? 0, roundId),
    {
      refetchInterval: 1000 * 60,
      enabled: isAuthenticated && !!roundId,
      staleTime: 60000,
    },
  )

  const { data: gameLeaderboard } = useQuery<LeaderboardResponse>(
    [QUERY_KEYS.GAME_LEADERBOARD, gameId],
    () => UserApi.getLeaderbard(gameId ?? 0, null, 1, 0),
    {
      enabled: isAuthenticated && !!gameId && needsLeaderboard,
      staleTime: 60000,
    },
  )

  const { data: roundLeaderboard } = useQuery<LeaderboardResponse>(
    [QUERY_KEYS.GAME_LEADERBOARD, gameId, roundId],
    () => UserApi.getLeaderbard(gameId ?? 0, roundId ?? 0, 1, 0),
    {
      enabled: isAuthenticated && !!gameId && !!roundId && needsLeaderboard,
      staleTime: 60000,
    },
  )

  const { winner: gameWinner, winnerScore: gameWinnerScore } = extractWinner(gameLeaderboard)
  const { winner: roundWinner, winnerScore: roundWinnerScore } = extractWinner(roundLeaderboard)

  return {
    roundPositionData,
    gamePositionData,
    gameWinner,
    gameWinnerScore,
    roundWinner,
    roundWinnerScore,
    isGamePositionTied: isPositionTied(gamePositionData),
    isRoundPositionTied: isPositionTied(roundPositionData),
  }
}

// Round-only counterpart to useScoreboardData. Game-level position/score/winner
// data is now delivered batched on the Game object (from /partners/{id}/games),
// so callers that already have that don't need the gamePositionData/gameLeaderboard
// queries above — only an explicit roundId lookup still needs a network round-trip.
export const useRoundPositionData = (
  gameId?: number,
  roundId?: number,
  needsLeaderboard = true,
) => {
  const { isAuthenticated } = useAuth()
  const { data: roundPositionData } = useQuery(
    [QUERY_KEYS.POSITION_DATA, roundId, gameId],
    () => UserApi.getPositionData(gameId ?? 0, roundId),
    {
      refetchInterval: 1000 * 60,
      enabled: isAuthenticated && !!roundId,
      staleTime: 60000,
    },
  )

  const { data: roundLeaderboard } = useQuery<LeaderboardResponse>(
    [QUERY_KEYS.GAME_LEADERBOARD, gameId, roundId],
    () => UserApi.getLeaderbard(gameId ?? 0, roundId ?? 0, 1, 0),
    {
      enabled: isAuthenticated && !!gameId && !!roundId && needsLeaderboard,
      staleTime: 60000,
    },
  )

  const { winner: roundWinner, winnerScore: roundWinnerScore } = extractWinner(roundLeaderboard)

  return {
    roundPositionData,
    roundWinner,
    roundWinnerScore,
    isRoundPositionTied: isPositionTied(roundPositionData),
  }
}

export const useRoundWinner = (gameId?: number, roundId?: number, enabled = true) => {
  const { isAuthenticated } = useAuth()
  const { data: roundLeaderboard } = useQuery<LeaderboardResponse>(
    [QUERY_KEYS.GAME_LEADERBOARD, gameId, roundId],
    () => UserApi.getLeaderbard(gameId ?? 0, roundId ?? 0, 1, 0),
    {
      enabled: isAuthenticated && !!gameId && !!roundId && enabled,
      staleTime: 60000,
    },
  )

  return extractWinner(roundLeaderboard)
}

// Lazy game-level lookup for the round/overall toggle. The batched game.position
// props cover the default (non-toggled) render, but they don't carry `index`, so
// tie detection needs this on-demand fetch — gated on `enabled` so it only fires
// once the viewer actively switches to the "Overall" view, never on initial load.
export const useGameToggleData = (gameId?: number, enabled = false, needsLeaderboard = true) => {
  const { isAuthenticated } = useAuth()
  const { data: gamePositionData } = useQuery(
    [QUERY_KEYS.POSITION_DATA, gameId],
    () => UserApi.getPositionData(gameId ?? 0, undefined),
    {
      refetchInterval: 1000 * 60,
      enabled: isAuthenticated && !!gameId && enabled,
      staleTime: 60000,
    },
  )

  const { data: gameLeaderboard } = useQuery<LeaderboardResponse>(
    [QUERY_KEYS.GAME_LEADERBOARD, gameId],
    () => UserApi.getLeaderbard(gameId ?? 0, null, 1, 0),
    {
      enabled: isAuthenticated && !!gameId && enabled && needsLeaderboard,
      staleTime: 60000,
    },
  )

  const { winner: gameWinner, winnerScore: gameWinnerScore } = extractWinner(gameLeaderboard)

  return {
    gamePositionData,
    gameWinner,
    gameWinnerScore,
    isGamePositionTied: isPositionTied(gamePositionData),
  }
}
